import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { format } from 'date-fns';
import { InvestmentRecord, AssetType } from '../services/api';
import { usePortfolio } from '../hooks/usePortfolio';
import { useModalAccessibility } from '../hooks/useModalAccessibility';
import StockSearchModal, { StockSearchResult } from './StockSearchModal';
import { DocumentIcon, CurrencyIcon, ArrowUpTrendIcon, ArrowDownTrendIcon, CloseIcon, AlertTriangleIcon, SearchIcon } from './Icons';
import './InvestmentForm.css';

interface StockOption {
  id: number;
  label: string;
  name: string;
  code?: string;
}

// 배치 입력 행 타입
interface BatchRow {
  id: string;
  date: string; // yyyy-MM-dd 형식
  // 주식 필드
  stockName: string;
  stockCode: string;
  quantity: string;
  buyPrice: string;
  buyReason: string;
  // 외화 거래 필드
  currencyPair: string;
  baseCurrency: string;
  quoteCurrency: string;
  exchangeRate: string;
  // 매도 필드
  selectedStockId: string;
  sellQuantity: string;
  sellPrice: string; // 매도 단가 (주당 가격)
  sellReason: string;
}

interface InvestmentFormProps {
  onSubmit: (record: any) => Promise<boolean>;
  onUpdate?: (recordId: number, record: any) => Promise<boolean>;
  onDelete?: (recordId: number) => void;
  onCancel: () => void;
  selectedDate: Date;
  existingRecords?: InvestmentRecord[];
  allInvestments?: InvestmentRecord[]; // 전체 투자 기록 (보유 수량 계산용)
  existingStocks?: StockOption[];
  initialEditingRecordId?: number;
}

// 배치 행 헬퍼 함수
let rowIdCounter = 0;
const createEmptyRow = (defaultDate?: Date): BatchRow => ({
  id: `row-${Date.now()}-${++rowIdCounter}`,
  date: defaultDate ? format(defaultDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
  stockName: '', stockCode: '', quantity: '', buyPrice: '', buyReason: '',
  currencyPair: '', baseCurrency: '', quoteCurrency: '', exchangeRate: '',
  selectedStockId: '', sellQuantity: '', sellPrice: '', sellReason: '',
});

const isBuyRowEmpty = (row: BatchRow, assetType: AssetType): boolean => {
  if (assetType === 'CURRENCY') {
    // 통화쌍은 자동 생성되므로 체크하지 않음
    return !row.baseCurrency && !row.quoteCurrency && !row.exchangeRate && !row.quantity && !row.buyReason;
  }
  return !row.stockName && !row.stockCode && !row.quantity && !row.buyPrice && !row.buyReason;
};

const isSellRowEmpty = (row: BatchRow): boolean =>
  !row.selectedStockId && !row.sellQuantity && !row.sellPrice && !row.sellReason;

const isRowEmpty = (row: BatchRow, type: 'buy' | 'sell', assetType: AssetType = 'STOCK'): boolean =>
  type === 'buy' ? isBuyRowEmpty(row, assetType) : isSellRowEmpty(row);

const isRowFilled = (row: BatchRow, type: 'buy' | 'sell', assetType: AssetType = 'STOCK'): boolean => {
  if (!row.date || !row.date.trim()) return false; // 날짜 필수
  if (type === 'buy') {
    if (assetType === 'CURRENCY') {
      // 외화 매수: 기준통화, 상대통화, 환율, 수량 필수
      return !!(row.baseCurrency && row.quoteCurrency) && !!row.exchangeRate && !!row.quantity;
    }
    return !!row.stockName.trim();
  }
  return !!row.selectedStockId;
};

const InvestmentForm: React.FC<InvestmentFormProps> = ({
  onSubmit,
  onUpdate,
  onDelete,
  onCancel,
  initialEditingRecordId,
  selectedDate,
  existingRecords = [],
  allInvestments = [],
  existingStocks = []
}) => {
  // ============================
  // 공통 State
  // ============================
  const [formType, setFormType] = useState<'buy' | 'sell'>('buy');
  const [showExistingRecords, setShowExistingRecords] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveResult, setSaveResult] = useState<{ success: number; fail: number } | null>(null);
  const [saveProgress, setSaveProgress] = useState<{ current: number; total: number } | null>(null);
  const [overlayOpacity, setOverlayOpacity] = useState(1.0); // 모달 투명도 (0.5 ~ 1.0)
  const shortcutStateRef = React.useRef({ isEditMode: false, isSubmitting: false });
  const batchSaveRef = React.useRef<(() => Promise<void>) | null>(null);
  const editSubmitRef = React.useRef<((e: React.FormEvent) => Promise<void>) | null>(null);
  const { summary: portfolioSummary } = usePortfolio();

  // ============================
  // 종목 검색 모달 State
  // ============================
  const [showStockSearch, setShowStockSearch] = useState(false);
  const [stockSearchTarget, setStockSearchTarget] = useState<{
    mode: 'batch' | 'edit';
    rowId?: string;
    initialQuery?: string;
  } | null>(null);
  const { modalRef } = useModalAccessibility(!showStockSearch, onCancel);

  // ============================
  // 배치 모드 State
  // ============================
  const [batchAssetType, setBatchAssetType] = useState<AssetType>('STOCK');
  const [batchRows, setBatchRows] = useState<BatchRow[]>([createEmptyRow()]);

  // ============================
  // 수정 모드 State
  // ============================
  const [editingRecordId, setEditingRecordId] = useState<number | null>(null);
  const [assetType, setAssetType] = useState<AssetType>('STOCK');
  const [editFormData, setEditFormData] = useState({
    stockName: '', stockCode: '', currencyPair: '', baseCurrency: '',
    quoteCurrency: '', exchangeRate: '', investmentRatio: '', quantity: '',
    buyReason: '', buyPrice: '', totalAmount: '', sellReason: '', sellQuantity: '',
    sellRatio: '', sellPrice: '', selectedStockId: ''
  });

  const isEditMode = editingRecordId !== null;
  shortcutStateRef.current = { isEditMode, isSubmitting };

  // ============================
  // 종목 검색 핸들러 (State 선언 이후)
  // ============================

  // 종목 검색 결과 선택 핸들러
  const handleStockSelect = useCallback((stock: StockSearchResult) => {
    if (!stockSearchTarget) return;

    if (stockSearchTarget.mode === 'batch' && stockSearchTarget.rowId) {
      setBatchRows(prev => {
        let updated = prev.map(row =>
          row.id === stockSearchTarget.rowId
            ? { ...row, stockName: stock.stockName, stockCode: stock.stockCode }
            : row
        );
        // 마지막 행에 입력이 생기면 새 행 추가
        const lastRow = updated[updated.length - 1];
        if (!isRowEmpty(lastRow, formType)) {
          updated = [...updated, createEmptyRow()];
        }
        return updated;
      });
      
      // 종목 선택 후 수량 필드로 포커스 이동
      setTimeout(() => {
        const quantityInput = document.getElementById(`quantity-${stockSearchTarget.rowId}`) as HTMLInputElement;
        if (quantityInput) {
          quantityInput.focus();
        }
      }, 100);
    } else if (stockSearchTarget.mode === 'edit') {
      setEditFormData(prev => ({
        ...prev,
        stockName: stock.stockName,
        stockCode: stock.stockCode
      }));
      
      // 수정 모드에서도 수량 필드로 포커스 이동
      setTimeout(() => {
        const quantityInput = document.getElementById('edit-quantity') as HTMLInputElement;
        if (quantityInput) {
          quantityInput.focus();
        }
      }, 100);
    }

    setShowStockSearch(false);
    setStockSearchTarget(null);
  }, [stockSearchTarget, formType]);

  // 종목 검색 모달 열기 (배치 모드)
  const openStockSearchForBatch = useCallback((rowId: string, currentName: string) => {
    setStockSearchTarget({ mode: 'batch', rowId, initialQuery: currentName });
    setShowStockSearch(true);
  }, []);

  // 종목 검색 모달 열기 (수정 모드)
  const openStockSearchForEdit = useCallback(() => {
    setStockSearchTarget({ mode: 'edit', initialQuery: editFormData.stockName });
    setShowStockSearch(true);
  }, [editFormData.stockName]);

  // ============================
  // 공통 헬퍼 함수
  // ============================
  const getCurrencyFromStockCode = (stockCode?: string) => {
    if (!stockCode) return 'KRW';
    if (/^[A-Z]+$/.test(stockCode)) return 'USD';
    if (/^\d+$/.test(stockCode)) return 'KRW';
    return 'KRW';
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('ko-KR');

  const formatCurrency = (amount?: number, currency: string = 'KRW') => {
    if (!amount) return '-';
    const formatted = new Intl.NumberFormat('ko-KR', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    }).format(amount);
    if (currency === 'USD') return `$${formatted}`;
    if (currency === 'KRW') return `${formatted}원`;
    return `${formatted} ${currency}`;
  };

  // ============================
  // 배치 모드 로직
  // ============================
  
  // formType 변경 시 배치 행 리셋
  useEffect(() => {
    if (!isEditMode) {
      setBatchRows([createEmptyRow(selectedDate)]);
      setSaveResult(null);
    }
  }, [formType, isEditMode, selectedDate]);

  // 저장 결과 자동 숨김
  useEffect(() => {
    if (saveResult) {
      const timer = setTimeout(() => setSaveResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [saveResult]);

  const handleBatchFieldChange = useCallback((rowId: string, field: keyof BatchRow, value: string) => {
    setBatchRows(prev => {
      let updated = prev.map(row =>
        row.id === rowId ? { ...row, [field]: value } : row
      );

      // 마지막 행에 입력이 시작되면 새 빈 행 추가
      const lastRow = updated[updated.length - 1];
      if (!isRowEmpty(lastRow, formType, batchAssetType)) {
        updated = [...updated, createEmptyRow(selectedDate)];
      }

      // 빈 비-마지막 행 자동 제거 (현재 편집 중인 행 제외)
      updated = updated.filter((row, index) => {
        if (index === updated.length - 1) return true; // 마지막 행 유지
        if (row.id === rowId) return true; // 현재 편집 중인 행 유지
        return !isRowEmpty(row, formType, batchAssetType);
      });

      return updated;
    });
  }, [formType, batchAssetType, selectedDate]);

  const handleDeleteRow = useCallback((rowId: string) => {
    setBatchRows(prev => {
      const filtered = prev.filter(row => row.id !== rowId);
      if (filtered.length === 0) return [createEmptyRow(selectedDate)];
      const lastRow = filtered[filtered.length - 1];
      if (!isRowEmpty(lastRow, formType, batchAssetType)) {
        return [...filtered, createEmptyRow(selectedDate)];
      }
      return filtered;
    });
  }, [formType, batchAssetType, selectedDate]);

  const filledCount = useMemo(() =>
    batchRows.filter(row => isRowFilled(row, formType, batchAssetType)).length
  , [batchRows, formType, batchAssetType]);

  const handleBatchSave = async () => {
    const filledRows = batchRows.filter(row => isRowFilled(row, formType, batchAssetType));
    if (filledRows.length === 0) return;

    setIsSubmitting(true);
    setSaveResult(null);
    setSaveProgress({ current: 0, total: filledRows.length });

    let successCount = 0;
    let failCount = 0;
    const failedRowIds = new Set<string>();
    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    for (let i = 0; i < filledRows.length; i++) {
      const row = filledRows[i];
      setSaveProgress({ current: i + 1, total: filledRows.length });
      
      try {
        // 매도 기록 검증
        if (formType === 'sell') {
          if (!row.selectedStockId) {
            const itemName = batchAssetType === 'CURRENCY' ? '통화' : '종목';
            alert(`행 ${i + 1}: 매도할 ${itemName}을 선택해주세요.`);
            failCount++;
            failedRowIds.add(row.id);
            continue;
          }
          
          const selectedStockId = parseInt(row.selectedStockId);
          if (isNaN(selectedStockId)) {
            const itemName = batchAssetType === 'CURRENCY' ? '통화' : '종목';
            alert(`행 ${i + 1}: 유효하지 않은 ${itemName} 선택입니다.`);
            failCount++;
            failedRowIds.add(row.id);
            continue;
          }
          
          const allRecords = allInvestments.length > 0 ? allInvestments : existingRecords;
          const targetRecord = allRecords.find(r => r.id === selectedStockId);
          if (!targetRecord) {
            const itemName = batchAssetType === 'CURRENCY' ? '통화' : '종목';
            alert(`행 ${i + 1}: 선택한 ${itemName}을 찾을 수 없습니다.`);
            failCount++;
            failedRowIds.add(row.id);
            continue;
          }
          
          const isCurrency = targetRecord.assetType === 'CURRENCY';
          const stockName = targetRecord.stockName;
          const stockCode = targetRecord.stockCode;
          const currencyPair = targetRecord.currencyPair;
          const baseCurrency = targetRecord.baseCurrency;
          const quoteCurrency = targetRecord.quoteCurrency;
          
          // 첫 매수 날짜 찾기
          const buyRecords = allRecords
            .filter(r => {
              if (r.isDeleted || r.type !== 'BUY') return false;
              if (isCurrency) {
                // 외환 거래: currencyPair 또는 baseCurrency/quoteCurrency로 식별
                return (currencyPair && r.currencyPair === currencyPair) ||
                       (baseCurrency && quoteCurrency && r.baseCurrency === baseCurrency && r.quoteCurrency === quoteCurrency);
              } else {
                // 주식 거래: stockName과 stockCode로 식별
                return r.stockName === stockName && r.stockCode === stockCode;
              }
            })
            .sort((a, b) => {
              const dateA = new Date(a.recordDate).getTime();
              const dateB = new Date(b.recordDate).getTime();
              if (dateA !== dateB) return dateA - dateB;
              return (a.id || 0) - (b.id || 0);
            });
          
          if (buyRecords.length === 0) {
            alert(`행 ${i + 1}: 해당 ${isCurrency ? '통화' : '종목'}의 매수 기록이 없습니다.`);
            failCount++;
            failedRowIds.add(row.id);
            continue;
          }
          
          const firstBuyDate = new Date(buyRecords[0].recordDate);
          const sellDate = row.date ? new Date(row.date) : new Date(dateStr);
          const firstBuyDateOnly = new Date(firstBuyDate.getFullYear(), firstBuyDate.getMonth(), firstBuyDate.getDate());
          const sellDateOnly = new Date(sellDate.getFullYear(), sellDate.getMonth(), sellDate.getDate());
          
          // 날짜 검증: 첫 매수 날짜 이후여야 함
          if (sellDateOnly < firstBuyDateOnly) {
            alert(`행 ${i + 1}: 매도 날짜(${format(sellDate, 'yyyy-MM-dd')})는 첫 매수 날짜(${format(firstBuyDate, 'yyyy-MM-dd')}) 이후여야 합니다.`);
            failCount++;
            failedRowIds.add(row.id);
            continue;
          }
          
          // 보유 수량 계산 (매도 시점 기준)
          const recordsBeforeSell = allRecords.filter(r => {
            if (r.isDeleted) return false;
            if (isCurrency) {
              // 외환 거래: currencyPair 또는 baseCurrency/quoteCurrency로 식별
              const matchesCurrency = (currencyPair && r.currencyPair === currencyPair) ||
                                     (baseCurrency && quoteCurrency && r.baseCurrency === baseCurrency && r.quoteCurrency === quoteCurrency);
              if (!matchesCurrency) return false;
            } else {
              // 주식 거래: stockName과 stockCode로 식별
              if (r.stockName !== stockName || r.stockCode !== stockCode) return false;
            }
            const recordDate = new Date(r.recordDate);
            const recordDateOnly = new Date(recordDate.getFullYear(), recordDate.getMonth(), recordDate.getDate());
            return recordDateOnly <= sellDateOnly;
          });
          
          recordsBeforeSell.sort((a, b) => {
            const dateA = new Date(a.recordDate).getTime();
            const dateB = new Date(b.recordDate).getTime();
            if (dateA !== dateB) return dateA - dateB;
            if (a.type !== b.type) return a.type === 'SELL' ? -1 : 1;
            return (a.id || 0) - (b.id || 0);
          });
          
          let holdingQty = 0;
          for (const record of recordsBeforeSell) {
            if (record.type === 'BUY' && record.quantity) {
              holdingQty += Number(record.quantity);
            } else if (record.type === 'SELL' && record.sellQuantity) {
              holdingQty -= Number(record.sellQuantity);
              if (holdingQty < 0) holdingQty = 0;
            }
          }
          
          // 수량 검증: 보유 수량을 초과할 수 없음
          if (row.sellQuantity) {
            const sellQty = parseFloat(row.sellQuantity);
            if (sellQty > holdingQty) {
              const unit = isCurrency ? (baseCurrency || '') : '주';
              alert(`행 ${i + 1}: 매도 수량(${sellQty.toLocaleString()}${unit})이 보유 수량(${holdingQty.toLocaleString()}${unit})을 초과할 수 없습니다.`);
              failCount++;
              failedRowIds.add(row.id);
              continue;
            }
            if (sellQty <= 0) {
              const unit = isCurrency ? (baseCurrency || '') : '주';
              alert(`행 ${i + 1}: 매도 수량은 0보다 커야 합니다.`);
              failCount++;
              failedRowIds.add(row.id);
              continue;
            }
          }
        }
        
        // 투자 비율 계산
        let investmentRatio = 0;
        if (formType === 'buy' && portfolioSummary?.totalSeed && portfolioSummary.totalSeed > 0) {
          if (batchAssetType === 'CURRENCY') {
            // 외화 거래: 수량 * 환율 = 원화 금액
            const qty = parseFloat(row.quantity) || 0;
            const rate = parseFloat(row.exchangeRate) || 0;
            if (qty > 0 && rate > 0) {
              const amount = qty * rate;
              investmentRatio = Math.min(100, (amount / portfolioSummary.totalSeed) * 100);
            }
          } else {
            // 주식 거래
            const price = parseFloat(row.buyPrice) || 0;
            const qty = parseFloat(row.quantity) || 0;
            if (price > 0 && qty > 0) {
              const stockCurrency = getCurrencyFromStockCode(row.stockCode);
              let amount = price * qty;
              if (stockCurrency === 'USD') amount *= 1300; // 임시 환율
              investmentRatio = Math.min(100, (amount / portfolioSummary.totalSeed) * 100);
            }
          }
        }

        const record: any = {
          date: row.date || dateStr, // 행의 날짜 사용, 없으면 기본 날짜
          type: formType,
          assetType: batchAssetType,
          investmentRatio,
          ...(batchAssetType === 'CURRENCY' && formType === 'buy' && {
            currencyPair: (row.baseCurrency && row.quoteCurrency) 
              ? `${row.baseCurrency}/${row.quoteCurrency}` 
              : (row.currencyPair || undefined),
            baseCurrency: row.baseCurrency || undefined,
            quoteCurrency: row.quoteCurrency || undefined,
            exchangeRate: row.exchangeRate ? parseFloat(row.exchangeRate) : undefined,
            quantity: row.quantity ? parseFloat(row.quantity) : undefined,
            buyReason: row.buyReason || undefined,
          }),
          ...(batchAssetType === 'STOCK' && {
            stockName: row.stockName,
            stockCode: row.stockCode || '',
            ...(formType === 'buy' && {
              quantity: row.quantity ? parseFloat(row.quantity) : undefined,
              buyPrice: row.buyPrice ? parseFloat(row.buyPrice) : undefined,
              buyReason: row.buyReason || undefined,
            }),
            ...(formType === 'sell' && {
              selectedStockId: row.selectedStockId || undefined,
              sellQuantity: row.sellQuantity ? parseFloat(row.sellQuantity) : undefined,
              sellPrice: row.sellPrice ? parseFloat(row.sellPrice) : undefined,
              sellReason: row.sellReason || undefined,
            }),
          }),
        };

        const success = await onSubmit(record);
        if (success) successCount++;
        else { failCount++; failedRowIds.add(row.id); }
      } catch (error) {
        failCount++;
        failedRowIds.add(row.id);
        console.error('배치 저장 오류:', error);
      }
    }

    // 성공한 행 제거, 실패한 행만 유지
    if (failedRowIds.size > 0) {
      setBatchRows(prev => {
        const remaining = prev.filter(row => failedRowIds.has(row.id));
        if (remaining.length === 0 || !isRowEmpty(remaining[remaining.length - 1], formType, batchAssetType)) {
          remaining.push(createEmptyRow(selectedDate));
        }
        return remaining;
      });
    } else {
      setBatchRows([createEmptyRow(selectedDate)]);
    }

    setIsSubmitting(false);
    setSaveProgress(null);
    setSaveResult({ success: successCount, fail: failCount });

    // 전부 성공 시 1.5초 후 모달 닫기
    if (failCount === 0) {
      setTimeout(() => onCancel(), 1500);
    }
  };

  // ============================
  // 수정 모드 로직
  // ============================
  const handleFillForm = (record: InvestmentRecord) => {
    setEditingRecordId(record.id);
    setFormType(record.type === 'BUY' ? 'buy' : 'sell');
    setAssetType(record.assetType || 'STOCK');
    setEditFormData({
      stockName: record.stockName || '',
      stockCode: record.stockCode || '',
      currencyPair: record.currencyPair || '',
      baseCurrency: record.baseCurrency || '',
      quoteCurrency: record.quoteCurrency || '',
      exchangeRate: record.exchangeRate?.toString() || '',
      investmentRatio: record.investmentRatio?.toString() || '',
      quantity: record.quantity?.toString() || '',
      buyReason: record.buyReason || '',
      buyPrice: record.pricePerShare?.toString() || '',
      totalAmount: record.totalAmount?.toString() || '',
      sellReason: record.sellReason || '',
      sellQuantity: record.sellQuantity?.toString() || '',
      sellRatio: record.sellRatio?.toString() || '',
      sellPrice: record.sellPrice?.toString() || '', // 기존 기록의 매도단가 불러오기
      selectedStockId: record.selectedStockId?.toString() || ''
    });
  };

  // 수정 모드 초기화
  useEffect(() => {
    if (initialEditingRecordId && existingRecords.length > 0) {
      const recordToEdit = existingRecords.find(r => r.id === initialEditingRecordId);
      if (recordToEdit) {
        handleFillForm(recordToEdit);
        return;
      }
    }
    setEditingRecordId(null);
  }, [selectedDate, initialEditingRecordId, existingRecords]);

  const handleCancelEdit = () => {
    setEditingRecordId(null);
    setFormType('buy');
    setAssetType('STOCK');
    onCancel(); // 모달 닫기
  };

  const handleCancelEditToBatch = () => {
    setEditingRecordId(null);
    setFormType('buy');
    setAssetType('STOCK');
    // 배치 입력 모드로 전환 (모달은 유지)
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  // 수정 모드 투자 비율 자동 계산
  const calculatedInvestmentRatio = useMemo(() => {
    if (formType !== 'buy' || !portfolioSummary?.totalSeed || portfolioSummary.totalSeed <= 0) return null;
    const totalSeed = portfolioSummary.totalSeed;
    let totalInvestmentAmount = 0;
    
    if (assetType === 'STOCK') {
      const price = parseFloat(editFormData.buyPrice) || 0;
      const qty = parseFloat(editFormData.quantity) || 0;
      if (price > 0 && qty > 0) {
        totalInvestmentAmount = price * qty;
        const stockCurrency = getCurrencyFromStockCode(editFormData.stockCode);
        if (stockCurrency === 'USD') totalInvestmentAmount *= 1300;
      }
    } else {
      const qty = parseFloat(editFormData.quantity) || 0;
      const rate = parseFloat(editFormData.exchangeRate) || 0;
      if (qty > 0 && rate > 0) totalInvestmentAmount = qty * rate;
    }
    
    if (totalInvestmentAmount > 0 && totalSeed > 0) {
      return Math.max(0.01, Math.min(100, (totalInvestmentAmount / totalSeed) * 100));
    }
    return null;
  }, [formType, assetType, editFormData.buyPrice, editFormData.quantity, 
      editFormData.exchangeRate, editFormData.stockCode, portfolioSummary]);

  // 보유 수량 계산 (매도용) - 매도 시점 기준
  const getHoldingQuantity = useMemo(() => {
    if (formType !== 'sell' || !editFormData.selectedStockId) return 0;
    const selectedStockId = parseInt(editFormData.selectedStockId);
    if (isNaN(selectedStockId)) return 0;
    
    // 선택된 매수 기록에서 종목명/코드 가져오기 (전체 기록에서 검색)
    const allRecords = allInvestments.length > 0 ? allInvestments : existingRecords;
    const targetRecord = allRecords.find(r => r.id === selectedStockId);
    if (!targetRecord) return 0;
    
    const isCurrency = targetRecord.assetType === 'CURRENCY';
    const stockName = targetRecord.stockName;
    const stockCode = targetRecord.stockCode;
    const currencyPair = targetRecord.currencyPair;
    const baseCurrency = targetRecord.baseCurrency;
    const quoteCurrency = targetRecord.quoteCurrency;
    
    // 매도 시점 날짜 (수정 모드일 때는 기록의 날짜, 아니면 선택된 날짜)
    const sellDate = isEditMode && editingRecordId 
      ? new Date(existingRecords.find(r => r.id === editingRecordId)?.recordDate || selectedDate)
      : selectedDate;
    
    // 매도 시점 이전의 기록만 고려 (같은 날짜의 매수 기록은 제외, 수정 모드일 때는 현재 수정 중인 매도 기록 제외)
    const recordsBeforeSell = allRecords.filter(r => {
      if (r.isDeleted) return false;
      
      // 외환 거래와 주식 거래를 구분하여 필터링
      if (isCurrency) {
        // 외환 거래: currencyPair 또는 baseCurrency/quoteCurrency로 식별
        const matchesCurrency = (currencyPair && r.currencyPair === currencyPair) ||
                               (baseCurrency && quoteCurrency && r.baseCurrency === baseCurrency && r.quoteCurrency === quoteCurrency);
        if (!matchesCurrency) return false;
      } else {
        // 주식 거래: stockName과 stockCode로 식별
        if (r.stockName !== stockName || r.stockCode !== stockCode) return false;
      }
      
      // 수정 모드일 때 현재 수정 중인 매도 기록 제외
      if (isEditMode && editingRecordId && r.id === editingRecordId) return false;
      
      const recordDate = new Date(r.recordDate);
      const sellDateOnly = new Date(sellDate.getFullYear(), sellDate.getMonth(), sellDate.getDate());
      const recordDateOnly = new Date(recordDate.getFullYear(), recordDate.getMonth(), recordDate.getDate());
      
      // 같은 날짜인 경우
      if (recordDateOnly.getTime() === sellDateOnly.getTime()) {
        // 같은 날짜의 매도 기록은 포함 (현재 수정 중인 것은 이미 제외됨)
        if (r.type === 'SELL') return true;
        // 같은 날짜의 매수 기록은 ID로 비교
        if (r.type === 'BUY') {
          // 수정 모드일 때, 현재 수정 중인 매도 기록의 ID와 비교
          if (isEditMode && editingRecordId) {
            // ID가 작으면 현재 기록 이전에 기록된 것이므로 포함
            // ID가 크면 현재 기록 이후에 기록된 것이므로 제외
            return (r.id || 0) < editingRecordId;
          }
          // 생성 모드일 때는 같은 날짜의 매수 기록 제외
          return false;
        }
      }
      
      // 매도일 이전의 기록만 포함
      return recordDateOnly < sellDateOnly;
    });
    
    // 날짜순으로 정렬 (같은 날짜면 매도가 먼저)
    recordsBeforeSell.sort((a, b) => {
      const dateA = new Date(a.recordDate).getTime();
      const dateB = new Date(b.recordDate).getTime();
      if (dateA !== dateB) return dateA - dateB;
      if (a.type !== b.type) return a.type === 'SELL' ? -1 : 1;
      return (a.id || 0) - (b.id || 0);
    });
    
    // 매도 시점의 보유 수량 계산
    let holdingQty = 0;
    for (const record of recordsBeforeSell) {
      if (record.type === 'BUY' && record.quantity) {
        holdingQty += Number(record.quantity);
      } else if (record.type === 'SELL' && record.sellQuantity) {
        holdingQty -= Number(record.sellQuantity);
        if (holdingQty < 0) holdingQty = 0;
      }
    }
    
    return holdingQty;
  }, [formType, editFormData.selectedStockId, allInvestments, existingRecords, isEditMode, editingRecordId, selectedDate]);

  // 첫 매수 날짜 계산 (매도 날짜 제한용)
  const firstBuyDate = useMemo(() => {
    if (formType !== 'sell' || !editFormData.selectedStockId) return null;
    const selectedStockId = parseInt(editFormData.selectedStockId);
    if (isNaN(selectedStockId)) return null;
    
    const allRecords = allInvestments.length > 0 ? allInvestments : existingRecords;
    const targetRecord = allRecords.find(r => r.id === selectedStockId);
    if (!targetRecord) return null;
    
    const isCurrency = targetRecord.assetType === 'CURRENCY';
    const stockName = targetRecord.stockName;
    const stockCode = targetRecord.stockCode;
    const currencyPair = targetRecord.currencyPair;
    const baseCurrency = targetRecord.baseCurrency;
    const quoteCurrency = targetRecord.quoteCurrency;
    
    // 해당 종목/통화의 첫 매수 기록 찾기
    const buyRecords = allRecords
      .filter(r => {
        if (r.isDeleted || r.type !== 'BUY') return false;
        if (isCurrency) {
          // 외환 거래: currencyPair 또는 baseCurrency/quoteCurrency로 식별
          return (currencyPair && r.currencyPair === currencyPair) ||
                 (baseCurrency && quoteCurrency && r.baseCurrency === baseCurrency && r.quoteCurrency === quoteCurrency);
        } else {
          // 주식 거래: stockName과 stockCode로 식별
          return r.stockName === stockName && r.stockCode === stockCode;
        }
      })
      .sort((a, b) => {
        const dateA = new Date(a.recordDate).getTime();
        const dateB = new Date(b.recordDate).getTime();
        if (dateA !== dateB) return dateA - dateB;
        return (a.id || 0) - (b.id || 0);
      });
    
    if (buyRecords.length === 0) return null;
    
    return new Date(buyRecords[0].recordDate);
  }, [formType, editFormData.selectedStockId, allInvestments, existingRecords]);

  const calculatedSellRatio = useMemo(() => {
    if (formType !== 'sell' || !editFormData.sellQuantity || getHoldingQuantity <= 0) return null;
    const sellQty = parseFloat(editFormData.sellQuantity);
    if (sellQty > 0 && getHoldingQuantity > 0) {
      return Math.max(0.01, Math.min(100, (sellQty / getHoldingQuantity) * 100));
    }
    return null;
  }, [formType, editFormData.sellQuantity, getHoldingQuantity]);

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || !editingRecordId || !onUpdate) return;
    
    // 매도 기록 검증
    if (formType === 'sell') {
      // 날짜 검증: 첫 매수 날짜 이후여야 함
      if (firstBuyDate) {
        const sellDate = new Date(selectedDate);
        const firstBuyDateOnly = new Date(firstBuyDate.getFullYear(), firstBuyDate.getMonth(), firstBuyDate.getDate());
        const sellDateOnly = new Date(sellDate.getFullYear(), sellDate.getMonth(), sellDate.getDate());
        
        if (sellDateOnly < firstBuyDateOnly) {
          alert(`매도 날짜는 첫 매수 날짜(${format(firstBuyDate, 'yyyy-MM-dd')}) 이후여야 합니다.`);
          return;
        }
      }
      
      // 수량 검증: 보유 수량을 초과할 수 없음
      if (editFormData.sellQuantity) {
        const sellQty = parseFloat(editFormData.sellQuantity);
        if (sellQty > getHoldingQuantity) {
          const targetRecord = (allInvestments.length > 0 ? allInvestments : existingRecords)
            .find(r => r.id === parseInt(editFormData.selectedStockId));
          const isCurrency = targetRecord?.assetType === 'CURRENCY';
          const unit = isCurrency ? (targetRecord?.baseCurrency || '') : '주';
          alert(`매도 수량(${sellQty.toLocaleString()}${unit})이 보유 수량(${getHoldingQuantity.toLocaleString()}${unit})을 초과할 수 없습니다.`);
          return;
        }
        if (sellQty <= 0) {
          alert('매도 수량은 0보다 커야 합니다.');
          return;
        }
      }
    }
    
    setIsSubmitting(true);

    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const record: any = {
        id: editingRecordId,
        date: dateStr,
        type: formType,
        assetType,
        stockName: editFormData.stockName,
        stockCode: editFormData.stockCode || '',
        investmentRatio: calculatedInvestmentRatio ?? (parseFloat(editFormData.investmentRatio) || 0),
        ...(assetType === 'CURRENCY' && {
          currencyPair: (editFormData.baseCurrency && editFormData.quoteCurrency) 
            ? `${editFormData.baseCurrency}/${editFormData.quoteCurrency}` 
            : undefined,
          baseCurrency: editFormData.baseCurrency || undefined,
          quoteCurrency: editFormData.quoteCurrency || undefined,
          exchangeRate: editFormData.exchangeRate ? parseFloat(editFormData.exchangeRate) : undefined
        }),
        ...(formType === 'buy' && {
          quantity: editFormData.quantity ? parseFloat(editFormData.quantity) : undefined,
          buyReason: editFormData.buyReason !== undefined && editFormData.buyReason !== null && editFormData.buyReason !== '' 
            ? editFormData.buyReason 
            : undefined,
          buyPrice: editFormData.buyPrice ? parseFloat(editFormData.buyPrice) : undefined,
          totalAmount: editFormData.totalAmount ? parseFloat(editFormData.totalAmount) : undefined
        }),
        ...(formType === 'sell' && {
          sellReason: editFormData.sellReason || undefined,
          sellQuantity: editFormData.sellQuantity ? parseFloat(editFormData.sellQuantity) : undefined,
          sellRatio: calculatedSellRatio ?? (editFormData.sellRatio ? parseFloat(editFormData.sellRatio) : undefined),
          sellPrice: editFormData.sellPrice?.trim()
            ? parseFloat(editFormData.sellPrice) : undefined,
          selectedStockId: editFormData.selectedStockId || undefined
        })
      };

      const success = await onUpdate(editingRecordId, record);
      if (success) {
        setSaveResult({ success: 1, fail: 0 });
        onCancel(); // 수정 완료 후 모달 닫기
      }
    } catch (error) {
      console.error('기록 수정 오류:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  batchSaveRef.current = handleBatchSave;
  editSubmitRef.current = handleEditSubmit;

  useEffect(() => {
    const onSaveShortcut = (e: KeyboardEvent) => {
      const { isEditMode: editingMode, isSubmitting: submitting } = shortcutStateRef.current;
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's';
      if (!isSave || submitting) return;
      e.preventDefault();

      if (editingMode) {
        void editSubmitRef.current?.(e as unknown as React.FormEvent);
      } else {
        void batchSaveRef.current?.();
      }
    };

    document.addEventListener('keydown', onSaveShortcut);
    return () => document.removeEventListener('keydown', onSaveShortcut);
  }, []);

  const getCurrencyLabel = () => {
    const currency = getCurrencyFromStockCode(editFormData.stockCode);
    if (currency === 'USD') return '(USD)';
    if (currency === 'KRW') return '(원)';
    return `(${currency})`;
  };

  // ============================
  // 렌더링
  // ============================
  return (
    <div className="investment-form-overlay">
      <div
        ref={modalRef}
        className={`investment-form modal-animated ${isEditMode ? '' : 'batch-mode'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="investment-form-title"
        style={{ opacity: overlayOpacity }}
      >
        {/* 헤더 */}
        <div className="form-header">
          <h2 id="investment-form-title">{isEditMode ? '기록 수정' : '매매 기록 작성'}</h2>
          <span className="date">{selectedDate.toLocaleDateString('ko-KR')}</span>
          <div className="header-controls">
            <div className="opacity-control">
              <label htmlFor="opacity-slider" title="모달 투명도 조절">
                <span className="opacity-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                  </svg>
                </span>
              </label>
              <input
                id="opacity-slider"
                type="range"
                min="0.5"
                max="1.0"
                step="0.1"
                value={overlayOpacity}
                onChange={(e) => setOverlayOpacity(parseFloat(e.target.value))}
                className="opacity-slider"
              />
            </div>
            <button className="close-btn" onClick={onCancel}><CloseIcon size={20} color="currentColor" /></button>
          </div>
        </div>

        {/* 저장 결과 토스트 */}
        {saveResult && (
          <div className={`save-result-toast ${saveResult.fail === 0 ? 'success' : 'partial'}`}>
            {saveResult.fail === 0
              ? `✅ ${saveResult.success}건의 기록이 저장되었습니다!`
              : <><span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><AlertTriangleIcon size={16} color="currentColor" /></span> {saveResult.success}건 성공, {saveResult.fail}건 실패</>
            }
          </div>
        )}

        {/* 기존 기록 섹션 */}
        {existingRecords && existingRecords.length > 0 && (
          <div className="existing-records-section">
            <div className="existing-records-header">
              <h3><span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '6px' }}><DocumentIcon size={18} color="currentColor" /></span> 기존 기록 ({existingRecords.length}개)</h3>
              <button
                type="button"
                className="toggle-records-btn"
                onClick={() => setShowExistingRecords(!showExistingRecords)}
              >
                {showExistingRecords ? '▲ 접기' : '▼ 펼치기'}
              </button>
            </div>

            {showExistingRecords && (
              <div className="existing-records-list">
                {existingRecords.map((record) => (
                  <div key={record.id} className="existing-record-card">
                    <div className="record-header">
                      <span className={`record-type ${record.type === 'BUY' ? 'buy' : 'sell'}`}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}>
                          {record.type === 'BUY' ? <ArrowUpTrendIcon size={14} color="currentColor" /> : <ArrowDownTrendIcon size={14} color="currentColor" />}
                        </span>
                        {record.type === 'BUY' ? '매수' : '매도'}
                      </span>
                      <span className="record-date">{formatDate(record.recordDate)}</span>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button type="button" className="fill-form-btn" onClick={() => handleFillForm(record)}>
                          ✏️ 수정
                        </button>
                        {onDelete && (
                          <button type="button" className="delete-record-btn"
                            onClick={() => { if (window.confirm('이 기록을 삭제하시겠습니까?')) onDelete(record.id); }}>
                            🗑️ 삭제
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="record-content">
                      <div className="record-item">
                        <strong>{record.assetType === 'CURRENCY' ? '통화쌍' : '종목'}:</strong>{' '}
                        {record.assetType === 'CURRENCY'
                          ? `${record.currencyPair || `${record.baseCurrency}/${record.quoteCurrency}`}`
                          : `${record.stockName} ${record.stockCode ? `(${record.stockCode})` : ''}`}
                      </div>
                      {record.type === 'BUY' && (
                        <>
                          {record.quantity && <div className="record-item"><strong>수량:</strong> {record.quantity.toLocaleString()}{record.assetType === 'CURRENCY' ? ` ${record.baseCurrency || ''}` : '주'}</div>}
                          {record.pricePerShare && <div className="record-item"><strong>매수가:</strong> {formatCurrency(record.pricePerShare, getCurrencyFromStockCode(record.stockCode))}</div>}
                          {record.totalAmount && <div className="record-item"><strong>총액:</strong> {formatCurrency(record.totalAmount, getCurrencyFromStockCode(record.stockCode))}</div>}
                        </>
                      )}
                      {record.type === 'SELL' && (
                        <>
                          {record.sellQuantity && <div className="record-item"><strong>매도수량:</strong> {record.sellQuantity.toLocaleString()}주</div>}
                          {record.sellPrice != null && (
                            <div className="record-item">
                              <strong>매도단가:</strong> {formatCurrency(record.sellPrice, getCurrencyFromStockCode(record.stockCode))}
                            </div>
                          )}
                          {record.realizedProfitRate != null && (
                            <div className="record-item">
                              <strong>손익률:</strong>
                              <span className={record.realizedProfitRate >= 0 ? 'profit' : 'loss'}>
                                {record.realizedProfitRate >= 0 ? '+' : ''}{record.realizedProfitRate.toFixed(2)}%
                              </span>
                            </div>
                          )}
                          {record.realizedProfitAmount != null && (
                            <div className="record-item">
                              <strong>실현손익:</strong>
                              <span className={record.realizedProfitAmount >= 0 ? 'profit' : 'loss'}>
                                {record.realizedProfitAmount >= 0 ? '+' : ''}{formatCurrency(record.realizedProfitAmount, getCurrencyFromStockCode(record.stockCode))}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 매수/매도 토글 */}
        <div className="form-type-selector">
          <button
            className={`type-btn ${formType === 'buy' ? 'active' : ''}`}
            onClick={() => { if (!isEditMode) setFormType('buy'); }}
            disabled={isEditMode}
          >
            매수
          </button>
          <button
            className={`type-btn ${formType === 'sell' ? 'active' : ''}`}
            onClick={() => { if (!isEditMode) setFormType('sell'); }}
            disabled={isEditMode}
          >
            매도
          </button>
        </div>

        {/* ============================
            수정 모드: 단건 폼
            ============================ */}
        {isEditMode ? (
          <>
            <div className="edit-mode-banner">
              <span><span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><DocumentIcon size={16} color="currentColor" /></span> 기록 수정 모드</span>
              <button type="button" className="cancel-edit-btn" onClick={handleCancelEditToBatch}>
                수정 취소 → 배치 입력
              </button>
            </div>

            <form onSubmit={handleEditSubmit}>
              {/* 자산 유형 */}
              <div className="form-group">
                <label>자산 유형 *</label>
                <div className="asset-type-selector">
                  <label className="asset-type-option">
                    <input type="radio" name="assetType" value="STOCK"
                      checked={assetType === 'STOCK'}
                      onChange={(e) => setAssetType(e.target.value as AssetType)} />
                    <span><span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><ArrowUpTrendIcon size={16} color="currentColor" /></span> 주식</span>
                  </label>
                  <label className="asset-type-option">
                    <input type="radio" name="assetType" value="CURRENCY"
                      checked={assetType === 'CURRENCY'}
                      onChange={(e) => setAssetType(e.target.value as AssetType)} />
                    <span><span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><CurrencyIcon size={16} color="currentColor" /></span> 외환</span>
                  </label>
                </div>
              </div>

              {formType === 'buy' ? (
                <>
                  {assetType === 'STOCK' ? (
                    <>
                      <div className="form-group">
                        <label>종목명 *</label>
                        <div className="stock-name-search-wrapper">
                          <input type="text" name="stockName" value={editFormData.stockName}
                            onChange={handleEditInputChange} placeholder="예: 삼성전자" required readOnly
                            onClick={openStockSearchForEdit}
                            style={{ cursor: 'pointer', backgroundColor: 'var(--color-bg-secondary)' }} />
                          <button type="button" className="stock-name-search-btn" onClick={openStockSearchForEdit} aria-label="종목 검색">
                            <SearchIcon size={18} color="currentColor" />
                          </button>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>종목코드</label>
                        <input type="text" name="stockCode" value={editFormData.stockCode}
                          onChange={handleEditInputChange} placeholder="자동 입력" readOnly
                          style={{ backgroundColor: 'var(--input-disabled-bg)', cursor: 'not-allowed' }} />
                        <small className="form-hint">종목 검색 시 자동으로 입력됩니다.</small>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="form-group">
                        <label>기준 통화 *</label>
                        <input type="text" name="baseCurrency" value={editFormData.baseCurrency}
                          onChange={handleEditInputChange} placeholder="예: USD" maxLength={3} required 
                          style={{ textTransform: 'uppercase' }} />
                      </div>
                      <div className="form-group">
                        <label>상대 통화 *</label>
                        <input type="text" name="quoteCurrency" value={editFormData.quoteCurrency}
                          onChange={handleEditInputChange} placeholder="예: KRW" maxLength={3} required 
                          style={{ textTransform: 'uppercase' }} />
                      </div>
                      <div className="form-group">
                        <label>환율 *</label>
                        <input type="number" name="exchangeRate" value={editFormData.exchangeRate}
                          onChange={handleEditInputChange} placeholder="예: 1300.50" step="0.0001" required />
                      </div>
                      <div className="form-group">
                        <label>수량 (기준 통화)</label>
                        <input type="number" id="edit-quantity" name="quantity" value={editFormData.quantity}
                          onChange={handleEditInputChange} placeholder="예: 1000" />
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <label>투자 비율 (%) *</label>
                    <input type="number" name="investmentRatio"
                      value={calculatedInvestmentRatio !== null ? calculatedInvestmentRatio.toFixed(2) : editFormData.investmentRatio}
                      readOnly placeholder="자동 계산됨" step="0.01"
                      style={{ backgroundColor: 'var(--input-disabled-bg)', cursor: 'not-allowed' }} />
                    <small className="form-hint">평단가와 수량을 입력하면 자동 계산됩니다.</small>
                  </div>

                  {assetType === 'STOCK' && (
                    <>
                      <div className="form-group">
                        <label>수량 (주)</label>
                        <input type="number" id="edit-quantity" name="quantity" value={editFormData.quantity}
                          onChange={handleEditInputChange} placeholder="주식 수량" />
                      </div>
                      <div className="form-group">
                        <label>매수 평단가 {getCurrencyLabel()}</label>
                        <input type="number" name="buyPrice" value={editFormData.buyPrice}
                          onChange={handleEditInputChange} placeholder={`주당 매수 가격`} step="0.01" min="0" />
                        <small className="form-hint">현지 통화 기준. 배당금은 자동 조회됩니다.</small>
                      </div>
                      <div className="form-group">
                        <label>총 투자금액 {getCurrencyLabel()} (선택사항)</label>
                        <input type="number" name="totalAmount" value={editFormData.totalAmount || ''}
                          onChange={handleEditInputChange} placeholder="총 투자금액 직접 입력" step="0.01" />
                        <small className="form-hint">평단가 대신 총액을 직접 입력할 수 있습니다.</small>
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <label>매수 이유 및 근거</label>
                    <textarea name="buyReason" value={editFormData.buyReason}
                      onChange={handleEditInputChange} placeholder="투자 판단 근거를 기록하세요" rows={4} />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>매도할 {assetType === 'CURRENCY' ? '통화' : '종목'} *</label>
                    <select name="selectedStockId" value={editFormData.selectedStockId}
                      onChange={handleEditInputChange} required>
                      <option value="">{assetType === 'CURRENCY' ? '통화를 선택하세요' : '종목을 선택하세요'}</option>
                      {existingStocks.map(stock => (
                        <option key={stock.id} value={stock.id.toString()}>{stock.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>매도 수량 {assetType === 'CURRENCY' ? '(통화)' : '(주)'} *</label>
                    <input type="number" name="sellQuantity" value={editFormData.sellQuantity}
                      onChange={handleEditInputChange} placeholder="매도 수량" required 
                      min="0.0001" step="0.01" max={getHoldingQuantity > 0 ? getHoldingQuantity : undefined} />
                    {editFormData.selectedStockId && (() => {
                      const targetRecord = (allInvestments.length > 0 ? allInvestments : existingRecords)
                        .find(r => r.id === parseInt(editFormData.selectedStockId));
                      const isCurrency = targetRecord?.assetType === 'CURRENCY';
                      const unit = isCurrency ? (targetRecord?.baseCurrency || '') : '주';
                      return (
                        <small className="form-hint">
                          보유 수량: {getHoldingQuantity.toLocaleString()}{unit} (최대 {getHoldingQuantity.toLocaleString()}{unit}까지 매도 가능)
                        </small>
                      );
                    })()}
                  </div>
                  <div className="form-group">
                    <label>매도 비율 (%)</label>
                    <input type="number" name="sellRatio"
                      value={calculatedSellRatio !== null ? calculatedSellRatio.toFixed(2) : editFormData.sellRatio}
                      readOnly placeholder="자동 계산됨" step="0.01"
                      style={{ backgroundColor: 'var(--input-disabled-bg)', cursor: 'not-allowed' }} />
                  </div>
                  <div className="form-group">
                    <label>매도 단가</label>
                    <input type="number" name="sellPrice" value={editFormData.sellPrice}
                      onChange={handleEditInputChange} placeholder="매도 단가 (주당 가격)" step="0.01" min="0" />
                  </div>
                  <div className="form-group">
                    <label>매도 사유</label>
                    <textarea name="sellReason" value={editFormData.sellReason}
                      onChange={handleEditInputChange} placeholder="매도 판단 근거를 기록하세요" rows={4} />
                  </div>
                </>
              )}

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCancelEdit}>수정 취소</button>
                <button type="submit" className="btn-submit" disabled={isSubmitting}>
                  {isSubmitting ? '저장 중...' : '기록 수정'}
                </button>
              </div>
            </form>
          </>
        ) : (
          /* ============================
             배치 모드: 스프레드시트형 테이블
             ============================ */
          <>
            {/* 자산 유형 탭 선택기 */}
            <div className="batch-asset-type-tabs">
              <button
                type="button"
                className={`batch-asset-tab ${batchAssetType === 'STOCK' ? 'active' : ''}`}
                onClick={() => {
                  if (batchAssetType !== 'STOCK') {
                    setBatchAssetType('STOCK');
                    setBatchRows([createEmptyRow(selectedDate)]);
                  }
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><ArrowUpTrendIcon size={16} color="currentColor" /></span> 주식
              </button>
              <button
                type="button"
                className={`batch-asset-tab ${batchAssetType === 'CURRENCY' ? 'active' : ''}`}
                onClick={() => {
                  if (batchAssetType !== 'CURRENCY') {
                    setBatchAssetType('CURRENCY');
                    setBatchRows([createEmptyRow(selectedDate)]);
                  }
                }}
              >
                💱 외환
              </button>
            </div>

            <div className="batch-input-section">
              {/* 테이블 헤더 - 자산 유형에 따라 컬럼 변경 */}
              <div className="batch-header-row">
                <div className="batch-col col-num">#</div>
                <div className="batch-col col-date">날짜 *</div>

                {batchAssetType === 'STOCK' ? (
                  /* 주식 헤더 */
                  formType === 'buy' ? (
                    <>
                      <div className="batch-col col-name">종목명 *</div>
                      <div className="batch-col col-code">코드</div>
                      <div className="batch-col col-qty">수량</div>
                      <div className="batch-col col-price">단가</div>
                      <div className="batch-col col-reason">매수 사유</div>
                    </>
                  ) : (
                    <>
                      <div className="batch-col col-select">종목 선택 *</div>
                      <div className="batch-col col-qty">수량</div>
                      <div className="batch-col col-rate">매도 단가</div>
                      <div className="batch-col col-reason">매도 사유</div>
                    </>
                  )
                ) : (
                  /* 외환 헤더 */
                  formType === 'buy' ? (
                    <>
                      <div className="batch-col col-base-currency">기준통화 *</div>
                      <div className="batch-col col-quote-currency">상대통화 *</div>
                      <div className="batch-col col-exchange-rate">환율 *</div>
                      <div className="batch-col col-qty">수량 *</div>
                      <div className="batch-col col-reason">메모</div>
                    </>
                  ) : (
                    <>
                      <div className="batch-col col-select">통화 선택 *</div>
                      <div className="batch-col col-qty">수량</div>
                      <div className="batch-col col-exchange-rate">매도 환율</div>
                      <div className="batch-col col-reason">매도 사유</div>
                    </>
                  )
                )}

                <div className="batch-col col-action"></div>
              </div>

              {/* 데이터 행 */}
              {batchRows.map((row, index) => {
                const empty = isRowEmpty(row, formType, batchAssetType);
                const isLast = index === batchRows.length - 1;
                const rowNum = isLast && empty ? '+' : (index + 1).toString();

                return (
                  <div key={row.id} className={`batch-row ${isLast && empty ? 'empty-row' : ''}`}>
                    <div className="batch-col col-num">
                      <span className="row-number">{rowNum}</span>
                    </div>

                    {/* 날짜 입력 */}
                    <div className="batch-col col-date">
                      <input
                        type="date"
                        value={row.date}
                        onChange={(e) => handleBatchFieldChange(row.id, 'date', e.target.value)}
                        required
                      />
                    </div>

                    {batchAssetType === 'STOCK' ? (
                      /* 주식 입력 행 */
                      formType === 'buy' ? (
                        <>
                          <div className="batch-col col-name">
                            <button
                              type="button"
                              className="stock-search-trigger"
                              onClick={() => openStockSearchForBatch(row.id, row.stockName)}
                              title="종목 검색"
                            >
                              {row.stockName ? (
                                <span className="stock-trigger-value">{row.stockName}</span>
                              ) : (
                                <span className="stock-trigger-placeholder"><SearchIcon size={14} color="currentColor" className="stock-trigger-icon" /> 종목 검색</span>
                              )}
                            </button>
                          </div>
                          <div className="batch-col col-code">
                            <input type="text" value={row.stockCode}
                              onChange={(e) => handleBatchFieldChange(row.id, 'stockCode', e.target.value)}
                              placeholder="코드" readOnly
                              style={{ backgroundColor: row.stockCode ? 'var(--input-filled-bg)' : 'transparent', cursor: 'default' }} />
                          </div>
                          <div className="batch-col col-qty">
                            <input type="number" id={`quantity-${row.id}`} value={row.quantity}
                              onChange={(e) => handleBatchFieldChange(row.id, 'quantity', e.target.value)}
                              placeholder="수량(주)" min="0" />
                          </div>
                          <div className="batch-col col-price">
                            <input type="number" value={row.buyPrice}
                              onChange={(e) => handleBatchFieldChange(row.id, 'buyPrice', e.target.value)}
                              placeholder="단가" step="0.01" min="0" />
                          </div>
                          <div className="batch-col col-reason">
                            <input type="text" value={row.buyReason}
                              onChange={(e) => handleBatchFieldChange(row.id, 'buyReason', e.target.value)}
                              placeholder="매수 이유" />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="batch-col col-select">
                            <select value={row.selectedStockId}
                              onChange={(e) => handleBatchFieldChange(row.id, 'selectedStockId', e.target.value)}>
                              <option value="">종목 선택</option>
                              {existingStocks.map(stock => (
                                <option key={stock.id} value={stock.id.toString()}>{stock.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="batch-col col-qty">
                            <input type="number" value={row.sellQuantity}
                              onChange={(e) => handleBatchFieldChange(row.id, 'sellQuantity', e.target.value)}
                              placeholder="수량" min="0" />
                          </div>
                          <div className="batch-col col-rate">
                            <input type="number" value={row.sellPrice}
                              onChange={(e) => handleBatchFieldChange(row.id, 'sellPrice', e.target.value)}
                              placeholder="매도 단가" step="0.01" min="0" />
                          </div>
                          <div className="batch-col col-reason">
                            <input type="text" value={row.sellReason}
                              onChange={(e) => handleBatchFieldChange(row.id, 'sellReason', e.target.value)}
                              placeholder="매도 사유" />
                          </div>
                        </>
                      )
                    ) : (
                      /* 외환 입력 행 */
                      formType === 'buy' ? (
                        <>
                          <div className="batch-col col-base-currency">
                            <input type="text" value={row.baseCurrency}
                              onChange={(e) => handleBatchFieldChange(row.id, 'baseCurrency', e.target.value.toUpperCase())}
                              placeholder="예: USD" maxLength={3}
                              style={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }} />
                          </div>
                          <div className="batch-col col-quote-currency">
                            <input type="text" value={row.quoteCurrency}
                              onChange={(e) => handleBatchFieldChange(row.id, 'quoteCurrency', e.target.value.toUpperCase())}
                              placeholder="예: KRW" maxLength={3}
                              style={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }} />
                          </div>
                          <div className="batch-col col-exchange-rate">
                            <input type="number" value={row.exchangeRate}
                              onChange={(e) => handleBatchFieldChange(row.id, 'exchangeRate', e.target.value)}
                              placeholder="환율" step="0.0001" min="0" />
                          </div>
                          <div className="batch-col col-qty">
                            <input type="number" id={`quantity-${row.id}`} value={row.quantity}
                              onChange={(e) => handleBatchFieldChange(row.id, 'quantity', e.target.value)}
                              placeholder="수량" step="0.01" min="0" />
                          </div>
                          <div className="batch-col col-reason">
                            <input type="text" value={row.buyReason}
                              onChange={(e) => handleBatchFieldChange(row.id, 'buyReason', e.target.value)}
                              placeholder="메모" />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="batch-col col-select">
                            <select value={row.selectedStockId}
                              onChange={(e) => handleBatchFieldChange(row.id, 'selectedStockId', e.target.value)}>
                              <option value="">통화 선택</option>
                              {existingStocks
                                .filter(s => allInvestments.find(r => r.id === s.id)?.assetType === 'CURRENCY')
                                .map(stock => (
                                  <option key={stock.id} value={stock.id.toString()}>{stock.label}</option>
                                ))
                              }
                            </select>
                          </div>
                          <div className="batch-col col-qty">
                            <input type="number" value={row.sellQuantity}
                              onChange={(e) => handleBatchFieldChange(row.id, 'sellQuantity', e.target.value)}
                              placeholder="수량" step="0.01" min="0" />
                          </div>
                          <div className="batch-col col-exchange-rate">
                            <input type="number" value={row.sellPrice}
                              onChange={(e) => handleBatchFieldChange(row.id, 'sellPrice', e.target.value)}
                              placeholder="매도 환율" step="0.0001" min="0" />
                          </div>
                          <div className="batch-col col-reason">
                            <input type="text" value={row.sellReason}
                              onChange={(e) => handleBatchFieldChange(row.id, 'sellReason', e.target.value)}
                              placeholder="매도 사유" />
                          </div>
                        </>
                      )
                    )}

                    <div className="batch-col col-action">
                      {!(isLast && empty) && (
                        <button type="button" className="batch-delete-btn"
                          onClick={() => handleDeleteRow(row.id)} title="이 행 삭제">
                          <CloseIcon size={14} color="currentColor" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 배치 모드 하단 */}
            <div className="batch-footer">
              <span className="batch-count">
                {filledCount > 0
                  ? <><strong>{filledCount}</strong>건 작성됨</>
                  : '항목을 입력하세요'
                }
              </span>

              {/* 저장 진행 표시 */}
              {saveProgress && (
                <span className="save-progress">
                  저장 중... ({saveProgress.current}/{saveProgress.total})
                </span>
              )}

              <div className="batch-actions">
                <button type="button" className="btn-cancel" onClick={onCancel}>
                  닫기
                </button>
                <button type="button" className="btn-batch-save"
                  onClick={handleBatchSave}
                  disabled={isSubmitting || filledCount === 0}>
                  {isSubmitting
                    ? `저장 중... (${saveProgress?.current || 0}/${saveProgress?.total || 0})`
                    : `일괄 저장 (${filledCount}건)`
                  }
                </button>
              </div>
            </div>
          </>
        )}

        {/* 종목 검색 모달 */}
        <StockSearchModal
          isOpen={showStockSearch}
          onClose={() => { setShowStockSearch(false); setStockSearchTarget(null); }}
          onSelect={handleStockSelect}
          initialQuery={stockSearchTarget?.initialQuery || ''}
        />
      </div>
    </div>
  );
};

export default InvestmentForm;
